import { add, compare, decimal, fromString, multiply, round, toString, zero, } from "./decimal.js";
/**
 * The scales the product uses, named once.
 *
 * ADR-0016 has the reasoning. In short: a document carries two decimals
 * because that is what an NF-e carries and what a customer is charged, while a
 * unit price carries six because `vUnCom` in the NF-e layout permits more than
 * two and a price of R$ 0,0325 per unit is ordinary.
 */
export const MONEY_SCALE = 2;
export const UNIT_PRICE_SCALE = 6;
export const QUANTITY_SCALE = 6;
export const PERCENT_SCALE = 6;
/**
 * The rule for commercial amounts.
 *
 * `half-up` because that is what Brazilian invoices do and what a customer
 * expects. It is a named constant rather than a default parameter so that a
 * fiscal calculation which must round differently has to say so, visibly.
 */
export const COMMERCIAL_ROUNDING = 'half-up';
/** R$ 0,00. */
export const ZERO_MONEY = zero(MONEY_SCALE);
export const money = (value) => round(fromString(value), MONEY_SCALE, COMMERCIAL_ROUNDING);
export const unitPrice = (value) => round(fromString(value), UNIT_PRICE_SCALE, COMMERCIAL_ROUNDING);
export const quantity = (value) => round(fromString(value), QUANTITY_SCALE, COMMERCIAL_ROUNDING);
export const percent = (value) => round(fromString(value), PERCENT_SCALE, COMMERCIAL_ROUNDING);
/** Reduces any value to a document amount. The only place that decides centavos. */
export const toMoney = (value, mode = COMMERCIAL_ROUNDING) => round(value, MONEY_SCALE, mode);
/**
 * Quantity × unit price, reduced to a document amount.
 *
 * One function rather than a multiply and a round at each call site, because
 * the rounding step is the one people forget and the omission is invisible
 * until a total disagrees with its lines by a centavo.
 */
export const lineTotal = (quantityValue, unitPriceValue) => toMoney(multiply(quantityValue, unitPriceValue));
/** `base × rate%`, reduced to a document amount. */
export const applyPercent = (base, rate) => toMoney(multiply(base, decimal(rate.units, rate.scale + 2)));
export const addMoney = (left, right) => toMoney(add(left, right));
export const isSameAmount = (left, right) => compare(left, right) === 0;
/**
 * What the wire carries: a string, always.
 *
 * Never a JSON number — those are IEEE-754 doubles, so the guarantee would be
 * lost at the boundary, including inside `JSON.parse` on a client we do not
 * control.
 */
export const toWire = (value) => toString(value);
/**
 * What a Brazilian reader sees: `R$ 1.234,56`.
 *
 * Formatted from the exact value rather than from a `number`, so a screen and a
 * printed document cannot disagree about the last centavo. `Intl` is not used
 * for the amount itself for that reason — it takes a `number`.
 */
export const formatMoney = (value, options = {}) => {
    const rounded = toMoney(value);
    const negative = rounded.units < 0n;
    const digits = (negative ? -rounded.units : rounded.units)
        .toString()
        .padStart(MONEY_SCALE + 1, '0');
    const whole = digits.slice(0, digits.length - MONEY_SCALE);
    const cents = digits.slice(-MONEY_SCALE);
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const symbol = options.symbol === false ? '' : 'R$ ';
    return `${negative ? '-' : ''}${symbol}${grouped},${cents}`;
};
/** `12,5000%` → `12,5%`, for a screen. */
export const formatPercent = (value) => {
    const text = toString(value).replace('.', ',');
    const trimmed = text.includes(',') ? text.replace(/,?0+$/, '') : text;
    return `${trimmed.length === 0 ? '0' : trimmed}%`;
};
/**
 * Reads what a person typed: `1.234,56`, `1234,56`, `R$ 12,50`, `12`.
 *
 * Separate from `fromString` on purpose. That one reads machine formats and is
 * strict, because a lenient parser there would accept `"1.234,56"` from a
 * database column as `1.234`. This one is lenient because a person typing into
 * a field is not a protocol.
 *
 * Returns null rather than throwing: an unfinished value in a field is a normal
 * state, not an error.
 */
export const parseHumanAmount = (input, scale = MONEY_SCALE) => {
    const text = input
        .trim()
        .replace(/^R\$\s*/i, '')
        .replace(/\s/g, '');
    if (text.length === 0)
        return null;
    const negative = text.startsWith('-');
    const body = negative ? text.slice(1) : text;
    if (!/^[\d.,]+$/.test(body))
        return null;
    const lastComma = body.lastIndexOf(',');
    const lastDot = body.lastIndexOf('.');
    // Whichever separator comes last is the decimal one — which is what
    // distinguishes '1.234,56' from '1,234.56'. When only one kind appears and it
    // could be either, three digits after it means thousands: '1.234' is one
    // thousand two hundred and thirty-four, not one and a bit.
    let normalized;
    if (lastComma === -1 && lastDot === -1) {
        normalized = body;
    }
    else if (lastComma > lastDot) {
        normalized = `${body.slice(0, lastComma).replace(/[.,]/g, '')}.${body.slice(lastComma + 1)}`;
    }
    else if (lastDot > lastComma) {
        const fraction = body.slice(lastDot + 1);
        normalized =
            fraction.length === 3 && lastComma === -1 && body.split('.').length > 1
                ? body.replace(/[.,]/g, '')
                : `${body.slice(0, lastDot).replace(/[.,]/g, '')}.${fraction}`;
    }
    else {
        normalized = body;
    }
    try {
        return round(fromString(`${negative ? '-' : ''}${normalized}`), scale, COMMERCIAL_ROUNDING);
    }
    catch {
        return null;
    }
};
//# sourceMappingURL=money.js.map